import { theme } from 'antd';
import dayjs from 'dayjs';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectBookingsByDeviceID } from 'reduxStore/bookings/selectors';
import { Empty, Flex, Segmented, Typography } from 'zui/Antd';
import ActionDropdown from './ActionDropdown';
import { Loader } from 'zComponents/atoms';
import { selectPeople } from 'reduxStore/people/selectors';

const { Text } = Typography;

const CurrentBookingsContent = ({ deviceID }) => {
	const {
		token: {
			fontWeightStrong,
			paddingSM,
			colorBgLayout,
			borderRadius,
			colorBorderSecondary,
			fontSizeLG,
			paddingXS,
			fontSizeSM,
			colorTextLabel,
			paddingMD
		}
	} = theme.useToken();

	// state
	const [currentTab, setCurrentTab] = useState('Current');

	// ✅ Redux state
	const allBookings = useSelector((state) => selectBookingsByDeviceID(state, deviceID));
	const people = useSelector(selectPeople);
	const { createBookingLoading } = useSelector((state) => state.Bookings);

	const { currentBookings, pastBookings, futureBookings } = useMemo(() => {
		const now = dayjs();
		const current = [];
		const past = [];
		const future = [];

		allBookings.forEach((booking) => {
			const startTime = dayjs(booking.startTime);
			const endTime = dayjs(booking.endTime);

			if (now.isBefore(startTime)) {
				future.push(booking);
			} else if (now.isAfter(endTime)) {
				past.push(booking);
			} else {
				current.push(booking);
			}
		});

		return { currentBookings: current, pastBookings: past, futureBookings: future };
	}, [allBookings]);

	const bookings = useMemo(() => {
		switch (currentTab) {
			case 'Current':
				return currentBookings;
			case 'Past':
				return pastBookings;
			case 'Future':
				return futureBookings;
			default:
				return currentBookings;
		}
	}, [currentTab, currentBookings, pastBookings, futureBookings]);

	const options = ['Past', 'Current', 'Future'];

	const handleSetSelectedTab = (value) => {
		setCurrentTab(value);
	};

	const formatDateRange = (startTime, endTime) => {
		const start = dayjs(startTime);
		const end = dayjs(endTime);
		return `${start.format('D MMMM YYYY')} - ${end.format('D MMMM YYYY')}`;
	};

	const calculateDaysRemaining = (endTime) => {
		const days = dayjs(endTime).diff(dayjs(), 'day');
		return `${days} ${days === 1 ? 'Day' : 'Days'} Remaining`;
	};

	if (createBookingLoading) {
		return (
			<Flex justify='center' style={{ width: '100%' }}>
				<Loader />
			</Flex>
		);
	}

	return (
		<>
			<Segmented
				onChange={handleSetSelectedTab}
				value={currentTab}
				options={options}
				style={{ marginBottom: paddingMD }}
			/>
			<Flex gap={paddingXS}>
				{bookings.length === 0 ? (
					<Flex justify='center' align='center' style={{ width: '100%' }}>
						<Empty
							description={`No ${currentTab.toLowerCase()} bookings exist`}
							image={Empty.PRESENTED_IMAGE_SIMPLE}
						/>
					</Flex>
				) : (
					<Flex vertical gap={paddingXS} style={{ width: '100%' }}>
						{bookings.map((booking) => {
							const person = people.find((p) => p.personID === booking?.assignedToPerson);

							return (
								<Flex
									key={booking?.bookingID}
									vertical
									style={{
										width: '100%',
										boxSizing: 'border-box',
										backgroundColor: colorBgLayout,
										borderRadius,
										border: `1px solid ${colorBorderSecondary}`,
										padding: paddingSM
									}}
								>
									{/* Header row */}
									<Flex justify='space-between' align='center' style={{ width: '100%', gap: paddingXS }}>
										<Text
											style={{
												color: colorTextLabel,
												fontSize: fontSizeLG,
												fontWeight: fontWeightStrong
											}}
											title={person?.displayName || 'Unknown'}
										>
											{person?.displayName || 'Unknown'}
										</Text>

										<Flex style={{ flex: 'none' }}>
											<ActionDropdown bookingID={booking?.bookingID} />
										</Flex>
									</Flex>

									{/* Secondary row */}
									<Flex justify='space-between' align='center' style={{ width: '100%', gap: paddingXS }}>
										<Text
											style={{
												fontSize: fontSizeSM,
												color: colorTextLabel,
												fontWeight: 500,
												textOverflow: 'ellipsis'
											}}
											title={person?.email || booking?.email || '-'}
										>
											{person?.email || booking?.email || '-'}
										</Text>

										<Text
											style={{
												fontSize: fontSizeSM,
												color: colorTextLabel,
												fontWeight: 500,
												whiteSpace: 'nowrap'
											}}
										>
											{currentTab === 'Current'
												? calculateDaysRemaining(booking?.endTime)
												: formatDateRange(booking?.startTime, booking?.endTime)}
										</Text>
									</Flex>
								</Flex>
							);
						})}
					</Flex>
				)}
			</Flex>
		</>
	);
};

export default CurrentBookingsContent;
